import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useDispatch } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { USER_DISPLAY_MSG } from '../../../Constants';
import runtimeConfigApi from '../../utils/runtimeConfigApi';
import RuntimeConfigTabs from './RuntimeConfigTabs';
import RuntimeConfigStatusBar from './RuntimeConfigStatusBar';
import RuntimeConfigForm from './RuntimeConfigForm';
import RuntimeConfigBrowseModal from './RuntimeConfigBrowseModal';
import PropagationBanner from './PropagationBanner';
import RemoveConfirmModal from './RemoveConfirmModal';
import {
  applyLocalDocumentChange,
  buildRemoveSuccessMessage,
  createEmptyDocument,
  getEntryCount,
  getEntryKey,
  getPropagationMessage,
  mergeMetadataFromResults,
  resolveAuthor,
} from './runtimeConfigUtils';
import './RuntimeConfigPanel.css';

const RuntimeConfigPanel = () => {
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const author = useMemo(() => resolveAuthor(accounts), [accounts]);

  const [activeTab, setActiveTab] = useState('cors');
  const [metadata, setMetadata] = useState([]);
  const [documents, setDocuments] = useState({ cors: null, frame: null, mime: null });
  const [entryCounts, setEntryCounts] = useState({});
  const [notFoundTabs, setNotFoundTabs] = useState({});
  const [accessError, setAccessError] = useState('');
  const [loadingBrowse, setLoadingBrowse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [propagationMessage, setPropagationMessage] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browsedTabs, setBrowsedTabs] = useState({});

  const loadedTabsRef = useRef(new Set());

  const handleAccessDenied = useCallback(() => {
    setAccessError('Super Admin required');
  }, []);

  const loadMetadata = useCallback(async () => {
    try {
      const response = await runtimeConfigApi.fetchMetadata();
      if (response.status === 403) {
        handleAccessDenied();
        return;
      }
      if (response.status >= 400) {
        toastr.error('', response.data?.message || 'Failed to load runtime config metadata');
        return;
      }
      if (response.data?.configs) {
        setMetadata(response.data.configs);
      }
    } catch {
      toastr.error('', 'Failed to load runtime config metadata');
    }
  }, [handleAccessDenied]);

  const loadDocument = useCallback(async (configId, { force = false } = {}) => {
    if (!force && loadedTabsRef.current.has(configId)) {
      return true;
    }

    setLoadingBrowse(true);
    try {
      const response = await runtimeConfigApi.fetchDocument(configId);
      if (response.status === 403) {
        handleAccessDenied();
        return null;
      }
      if (response.status === 404) {
        setNotFoundTabs((prev) => ({ ...prev, [configId]: true }));
        const empty = createEmptyDocument(configId);
        setDocuments((prev) => ({ ...prev, [configId]: empty }));
        setEntryCounts((prev) => ({ ...prev, [configId]: 0 }));
        setBrowsedTabs((prev) => ({ ...prev, [configId]: true }));
        loadedTabsRef.current.add(configId);
        return empty;
      }
      if (response.status >= 400) {
        toastr.error('', response.data?.message || 'Failed to load config');
        return null;
      }
      if (response.data?.document) {
        const doc = response.data.document;
        setNotFoundTabs((prev) => ({ ...prev, [configId]: false }));
        setDocuments((prev) => ({ ...prev, [configId]: doc }));
        setEntryCounts((prev) => ({
          ...prev,
          [configId]: getEntryCount(configId, doc),
        }));
        setBrowsedTabs((prev) => ({ ...prev, [configId]: true }));
        loadedTabsRef.current.add(configId);
        return doc;
      }
    } catch {
      toastr.error('', 'Failed to load config');
    } finally {
      setLoadingBrowse(false);
    }
    return null;
  }, [handleAccessDenied]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const handleBrowse = async () => {
    setBrowseOpen(true);
    await loadDocument(activeTab, { force: true });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPrefill(null);
    setPropagationMessage('');
    setBrowseOpen(false);
  };

  const handleApply = async (
    { operation, entry, previousKey },
    { suppressPropagationBanner = false } = {}
  ) => {
    if (!author) {
      toastr.error('', 'Author could not be resolved from session');
      return { success: false };
    }
    setSubmitting(true);
    setAccessError('');
    try {
      const entryKey = getEntryKey(activeTab, entry);
      const keyChanged = Boolean(
        operation === 'merge' && previousKey && entryKey && previousKey !== entryKey
      );

      if (keyChanged) {
        const removeEntry = activeTab === 'mime'
          ? { extension: previousKey }
          : { pattern: previousKey };
        const removeResponse = await runtimeConfigApi.applyChange({
          configId: activeTab,
          operation: 'remove',
          author,
          entry: removeEntry,
        });
        if (removeResponse.status === 403) {
          handleAccessDenied();
          return { success: false };
        }
        if (removeResponse.status >= 400) {
          toastr.error('', removeResponse.data?.message || 'Failed to remove previous entry');
          return { success: false };
        }
      }

      const response = await runtimeConfigApi.applyChange({
        configId: activeTab,
        operation,
        author,
        entry,
      });
      if (response.status === 403) {
        handleAccessDenied();
        return { success: false };
      }
      if (response.status >= 400) {
        toastr.error('', response.data?.message || 'Failed to apply change');
        return { success: false };
      }

      const message = getPropagationMessage(response.data?.propagation);
      if (!suppressPropagationBanner) {
        setPropagationMessage(message);
      }
      if (response.data?.results?.length) {
        setMetadata((prev) => mergeMetadataFromResults(prev, response.data.results, author));
      }

      setDocuments((prev) => {
        const updated = applyLocalDocumentChange(
          activeTab,
          prev[activeTab] || createEmptyDocument(activeTab),
          operation,
          entry,
          previousKey
        );
        setEntryCounts((counts) => ({
          ...counts,
          [activeTab]: getEntryCount(activeTab, updated),
        }));
        return { ...prev, [activeTab]: updated };
      });
      if (browseOpen) {
        setBrowsedTabs((prev) => ({ ...prev, [activeTab]: true }));
      }
      return { success: true, message };
    } catch {
      toastr.error('', 'Failed to apply change');
      return { success: false };
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveRequest = (target, { fromForm = false } = {}) => {
    setRemoveModal({ target, fromForm });
  };

  const handleConfirmRemove = async () => {
    if (!removeModal?.target) {
      return;
    }
    const { target, fromForm } = removeModal;
    const result = await handleApply(
      { operation: 'remove', entry: target },
      { suppressPropagationBanner: true }
    );
    if (result.success) {
      const message = buildRemoveSuccessMessage(activeTab, target);
      dispatch({ type: USER_DISPLAY_MSG, response: { status_code: 200, message } });
      setRemoveModal(null);
      if (fromForm) {
        setFormResetKey((key) => key + 1);
      }
    }
  };

  const handleDismissRemoveModal = () => {
    setRemoveModal(null);
  };

  const clearPrefill = useCallback(() => setPrefill(null), []);

  const handleEdit = (prefillData) => {
    setPrefill(prefillData);
    document.querySelector('.runtime-config-form-section')?.scrollIntoView?.({ behavior: 'smooth' });
  };

  const activeMeta = metadata.find((item) => item.configId === activeTab);

  if (accessError === 'Super Admin required') {
    return (
      <div className="runtime-config-panel" data-testid="runtime-config-panel">
        <div className="runtime-config-banner runtime-config-banner-error" role="alert">
          Super Admin required
        </div>
      </div>
    );
  }

  return (
    <div className="runtime-config-panel" data-testid="runtime-config-panel">
      <PropagationBanner message={propagationMessage} onDismiss={() => setPropagationMessage('')} />

      <RuntimeConfigTabs
        activeTab={activeTab}
        entryCounts={entryCounts}
        browsedTabs={browsedTabs}
        loadingBrowse={loadingBrowse}
        onTabChange={handleTabChange}
        onBrowse={handleBrowse}
      />

      <RuntimeConfigStatusBar
        configId={activeTab}
        metadataItem={activeMeta}
        document={documents[activeTab]}
        entryCount={entryCounts[activeTab]}
      />

      <RuntimeConfigForm
        key={activeTab}
        configId={activeTab}
        prefill={prefill}
        submitting={submitting}
        formResetKey={formResetKey}
        onApply={handleApply}
        onRequestRemove={(entry) => handleRemoveRequest(entry, { fromForm: true })}
        onClear={() => setPrefill(null)}
        onClearPrefill={clearPrefill}
      />

      {browseOpen && (
        <RuntimeConfigBrowseModal
          configId={activeTab}
          document={documents[activeTab]}
          loading={loadingBrowse}
          notFound={Boolean(notFoundTabs[activeTab])}
          submitting={submitting}
          onEdit={handleEdit}
          onRemove={handleRemoveRequest}
          onClose={() => setBrowseOpen(false)}
        />
      )}

      <RemoveConfirmModal
        target={removeModal?.target}
        submitting={submitting && Boolean(removeModal?.target)}
        onConfirm={handleConfirmRemove}
        onCancel={handleDismissRemoveModal}
      />
    </div>
  );
};

export default RuntimeConfigPanel;
